import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, CalendarClock, MessageSquare } from "lucide-react";
import { Container, Eyebrow, DisplayHeading, BodyCopy, Reveal } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import { submitDemoRequest } from "@/lib/api";

const INTERESTS = ["AI Security Testing", "AI Governance", "ISO/IEC 42001 Readiness", "AI Assurance", "Enterprise Platform", "Agentic AI", "Other"];
const SIZES = ["1–50", "51–200", "201–1,000", "1,001–5,000", "5,000+"];
const SYSTEMS = ["1–5", "6–20", "21–50", "50+"];

// text-base below sm: anything under 16px makes iOS Safari zoom the page the
// moment a field takes focus, and it never zooms back out.
const fieldCls = "w-full rounded-md border border-line bg-ink px-4 py-3 text-base text-content placeholder:text-content-faint transition-colors focus:border-brand/50 focus:outline-none sm:text-sm";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.18em] text-content-faint";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", work_email: "", company: "", role: "", country: "", org_size: "", ai_systems: "", primary_interest: "", message: "", consent: false, website: "",
  });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.work_email || !form.company) return;
    setStatus("loading");
    try {
      await submitDemoRequest(form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <>
      <section className="relative overflow-hidden pb-8 pt-28 sm:pb-10 sm:pt-36 md:pt-44">
        <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-50" />
        <div className="pointer-events-none absolute -top-16 left-1/2 h-[240px] w-[400px] -translate-x-1/2 rounded-full bg-brand/[0.05] blur-[100px] sm:-top-24 sm:h-[380px] sm:w-[720px] sm:blur-[130px]" />
        <Container className="relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Eyebrow tone="brand">Contact</Eyebrow>
          </motion.div>
          <DisplayHeading as="h1" className="mt-6 max-w-3xl text-[34px] leading-[1.06] sm:mt-7 sm:text-5xl md:text-6xl">
            <MaskedLinesInView lines={["Let’s make", "your AI provable."]} />
          </DisplayHeading>
        </Container>
      </section>

      <Container className="pb-20 sm:pb-28">
        <div className="grid gap-8 sm:gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          {/* left: two paths */}
          <Reveal>
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-line bg-panel-raised p-6">
                <CalendarClock size={22} className="text-brand-soft" />
                <h3 className="mt-4 font-display text-xl text-content">Book a Demo</h3>
                <p className="mt-2 text-sm leading-relaxed text-content-muted">See the platform test, govern and prove a live demo AI system end to end.</p>
              </div>
              <div className="rounded-lg border border-line bg-panel-raised p-6">
                <MessageSquare size={22} className="text-brand" />
                <h3 className="mt-4 font-display text-xl text-content">Talk to an Assurance Specialist</h3>
                <p className="mt-2 text-sm leading-relaxed text-content-muted">Discuss AI security testing, governance or ISO/IEC 42001 readiness for your estate.</p>
              </div>
              <div className="mt-2 flex flex-col gap-3 rounded-lg border border-line bg-panel-raised/50 p-6 font-mono text-[12px] text-content-muted">
                <div><span className="text-content-faint">EMAIL</span><br />info@meiraai.com</div>
                <div><span className="text-content-faint">BASED IN</span><br />United Arab Emirates</div>
              </div>
            </div>
          </Reveal>

          {/* right: form */}
          <Reveal i={1}>
            <div className="relative overflow-hidden rounded-lg border border-line bg-panel-raised p-5 sm:p-7 md:p-9">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div key="success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center py-16 text-center" data-testid="contact-success">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-valid/30 bg-valid/10 valid-glow">
                      <CheckCircle2 size={30} className="text-valid" />
                    </div>
                    <h3 className="mt-6 font-display text-2xl text-content">Thank you. Your request has been received.</h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-content-muted">
                      An assurance specialist will review your details. In the meantime, you can explore
                      how verification works.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={submit} initial={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col gap-5" data-testid="contact-form">
                    {/* honeypot — hidden from real users */}
                    <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" tabIndex={-1}>
                      <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Name *</label>
                        <input data-testid="contact-name" required value={form.name} onChange={set("name")} className={fieldCls} placeholder="Your name" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Work email *</label>
                        <input data-testid="contact-email" required type="email" value={form.work_email} onChange={set("work_email")} className={fieldCls} placeholder="you@company.com" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Company *</label>
                        <input data-testid="contact-company" required value={form.company} onChange={set("company")} className={fieldCls} placeholder="Company name" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Role</label>
                        <input data-testid="contact-role" value={form.role} onChange={set("role")} className={fieldCls} placeholder="e.g. CISO" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Country</label>
                        <input data-testid="contact-country" value={form.country} onChange={set("country")} className={fieldCls} placeholder="Country" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Organization size</label>
                        <select data-testid="contact-size" value={form.org_size} onChange={set("org_size")} className={fieldCls}>
                          <option value="">Select…</option>
                          {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Number of AI systems</label>
                        <select data-testid="contact-systems" value={form.ai_systems} onChange={set("ai_systems")} className={fieldCls}>
                          <option value="">Select…</option>
                          {SYSTEMS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className={labelCls}>Primary interest</label>
                        <select data-testid="contact-interest" value={form.primary_interest} onChange={set("primary_interest")} className={fieldCls}>
                          <option value="">Select…</option>
                          {INTERESTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className={labelCls}>Message</label>
                      <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} className={fieldCls} placeholder="Tell us a little about your AI…" />
                    </div>
                    <label className="flex items-start gap-3 text-[13px] leading-relaxed text-content-muted">
                      <input type="checkbox" data-testid="contact-consent" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-5 w-5 shrink-0 accent-brand sm:mt-1 sm:h-4 sm:w-4" />
                      I agree that Meira AI may store and process my details to respond to this request, in line with its privacy policy.
                    </label>
                    {status === "error" && <p className="text-sm text-danger">Something went wrong. Please try again.</p>}
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={status === "loading"}
                      className="mt-1 inline-flex min-h-[52px] w-full items-center justify-center rounded-full bg-brand px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-soft disabled:opacity-60 sm:w-auto"
                    >
                      {status === "loading" ? "Sending…" : "Request a Consultation"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </Container>
    </>
  );
}
